import CampaignManager, { CampaignLevel } from './CampaignManager';

export default class CampaignUI {
    private campaignManager: CampaignManager;
    private container: HTMLDivElement | null = null;
    private onLevelSelected: ((level: CampaignLevel) => void) | null = null;

    constructor(campaignManager: CampaignManager) {
        this.campaignManager = campaignManager;
    }
    
    /**
     * Show level select with soft multiplayer toggle
     */
    public showLevelSelectWithSettings(onLevelSelected: (level: CampaignLevel, softMultiplayer: boolean) => void): void {
        this.onLevelSelected = (level: CampaignLevel) => {
            const settings = this.campaignManager.getCampaignSettings();
            onLevelSelected(level, settings.softMultiplayer);
        };
        this.createUI();
    }

    public showLevelSelect(onLevelSelected: (level: CampaignLevel) => void): void {
        this.onLevelSelected = onLevelSelected;
        this.createUI();
    }

    public hide(): void {
        if (this.container && this.container.parentElement) {
            this.container.parentElement.removeChild(this.container);
        }
        this.container = null;
    }

    private createUI(): void {
        // Remove existing UI if present
        this.hide();

        this.container = document.createElement('div');
        this.container.id = 'campaign-ui';
        this.container.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 1);
            z-index: 10000;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            color: white;
            font-family: 'Arial', sans-serif;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 20px;
            -webkit-overflow-scrolling: touch;
        `;

        // Content wrapper to center content but allow scrolling
        const contentWrapper = document.createElement('div');
        contentWrapper.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 1200px;
            padding: 40px 20px;
        `;
        
        // Title
        const title = document.createElement('h1');
        title.textContent = 'Campaign';
        title.style.cssText = `
            font-size: 48px;
            margin-bottom: 30px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
        `;
        contentWrapper.appendChild(title);

        // Progress bar
        const progress = this.campaignManager.getProgress();
        const progressContainer = document.createElement('div');
        progressContainer.style.cssText = `
            width: 100%;
            max-width: 600px;
            margin-bottom: 40px;
        `;
        
        const progressText = document.createElement('div');
        progressText.textContent = `Progress: ${progress.completed}/${progress.total} (${Math.round(progress.percentage)}%)`;
        progressText.style.cssText = `
            text-align: center;
            margin-bottom: 10px;
            font-size: 18px;
        `;
        progressContainer.appendChild(progressText);

        const progressBar = document.createElement('div');
        progressBar.style.cssText = `
            width: 100%;
            height: 30px;
            background: rgba(255,255,255,0.2);
            border-radius: 15px;
            overflow: hidden;
        `;
        
        const progressFill = document.createElement('div');
        progressFill.style.cssText = `
            height: 100%;
            width: ${progress.percentage}%;
            background: linear-gradient(90deg, #4CAF50, #8BC34A);
            transition: width 0.3s ease;
        `;
        progressBar.appendChild(progressFill);
        progressContainer.appendChild(progressBar);
        contentWrapper.appendChild(progressContainer);

        // Worlds container
        const worldsContainer = document.createElement('div');
        worldsContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 30px;
            width: 100%;
        `;

        const worlds = this.campaignManager.getAllWorlds();
        worlds.forEach((world, worldIndex) => {
            const worldCard = this.createWorldCard(world, worldIndex);
            worldsContainer.appendChild(worldCard);
        });

        contentWrapper.appendChild(worldsContainer);

        // Soft multiplayer toggle
        const settingsContainer = document.createElement('div');
        settingsContainer.style.cssText = `
            margin-top: 30px;
            padding: 20px;
            background: rgba(255,255,255,0.1);
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 15px;
        `;
        
        const toggleLabel = document.createElement('label');
        toggleLabel.textContent = 'Soft Multiplayer (see other players, chat enabled)';
        toggleLabel.style.cssText = `
            font-size: 18px;
            cursor: pointer;
            user-select: none;
        `;
        
        const toggle = document.createElement('input');
        toggle.type = 'checkbox';
        const settings = this.campaignManager.getCampaignSettings();
        toggle.checked = settings.softMultiplayer;
        toggle.style.cssText = `
            width: 24px;
            height: 24px;
            cursor: pointer;
        `;
        toggle.onchange = () => {
            this.campaignManager.saveCampaignSettings({ softMultiplayer: toggle.checked });
        };
        
        toggleLabel.appendChild(toggle);
        settingsContainer.appendChild(toggleLabel);
        contentWrapper.appendChild(settingsContainer);

        // Close button
        const closeButton = document.createElement('button');
        closeButton.textContent = 'Close';
        closeButton.style.cssText = `
            margin-top: 30px;
            padding: 15px 40px;
            font-size: 20px;
            background: #333;
            color: white;
            border: 2px solid #555;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
        `;
        closeButton.onmouseenter = () => {
            closeButton.style.background = '#444';
        };
        closeButton.onmouseleave = () => {
            closeButton.style.background = '#333';
        };
        closeButton.onclick = () => this.hide();
        contentWrapper.appendChild(closeButton);

        this.container.appendChild(contentWrapper);
        document.body.appendChild(this.container);
        
        // Scroll to top when UI is shown
        this.container.scrollTop = 0;
    }

    private createWorldCard(world: any, worldIndex: number): HTMLDivElement {
        const card = document.createElement('div');
        card.style.cssText = `
            background: rgba(255,255,255,0.1);
            border-radius: 12px;
            padding: 20px;
            border: 2px solid rgba(255,255,255,0.2);
        `;

        const worldTitle = document.createElement('h2');
        worldTitle.textContent = `${world.name} (World ${worldIndex + 1})`;
        worldTitle.style.cssText = `
            font-size: 28px;
            margin-bottom: 15px;
            color: #FFD700;
        `;
        card.appendChild(worldTitle);

        const levelsContainer = document.createElement('div');
        levelsContainer.style.cssText = `
            display: flex;
            gap: 15px;
            flex-wrap: wrap;
        `;

        world.levels.forEach((level: CampaignLevel, levelIndex: number) => {
            const levelButton = this.createLevelButton(level, worldIndex, levelIndex);
            levelsContainer.appendChild(levelButton);
        });

        card.appendChild(levelsContainer);
        return card;
    }

    private createLevelButton(level: CampaignLevel, worldIndex: number, levelIndex: number): HTMLButtonElement {
        const button = document.createElement('button');
        const isUnlocked = this.campaignManager.isLevelUnlocked(worldIndex, levelIndex);
        const isCurrent = this.campaignManager.getCurrentLevel()?.seed === level.seed;

        button.textContent = level.completed ? '✓' : String(levelIndex + 1);
        button.style.cssText = `
            width: 80px;
            height: 80px;
            font-size: 24px;
            font-weight: bold;
            border-radius: 8px;
            border: 3px solid;
            cursor: ${isUnlocked ? 'pointer' : 'not-allowed'};
            transition: all 0.2s;
            position: relative;
        `;

        if (isCurrent) {
            button.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
            button.style.borderColor = '#FFD700';
            button.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.5)';
        } else if (level.completed) {
            button.style.background = 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)';
            button.style.borderColor = '#4CAF50';
        } else if (isUnlocked) {
            button.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
            button.style.borderColor = '#888';
        } else {
            button.style.background = '#333';
            button.style.borderColor = '#555';
            button.style.opacity = '0.5';
        }

        if (isUnlocked) {
            button.onmouseenter = () => {
                if (!isCurrent) {
                    button.style.transform = 'scale(1.1)';
                    button.style.boxShadow = '0 0 15px rgba(255,255,255,0.3)';
                }
            };
            button.onmouseleave = () => {
                if (!isCurrent) {
                    button.style.transform = 'scale(1)';
                    button.style.boxShadow = 'none';
                }
            };
            button.onclick = () => {
                if (this.onLevelSelected) {
                    this.onLevelSelected(level);
                    this.hide();
                }
            };
        }

        // Difficulty indicator
        const difficultyBar = document.createElement('div');
        difficultyBar.style.cssText = `
            position: absolute;
            bottom: 5px;
            left: 5px;
            right: 5px;
            height: 3px;
            background: rgba(0,0,0,0.3);
            border-radius: 2px;
        `;
        const difficultyFill = document.createElement('div');
        difficultyFill.style.cssText = `
            height: 100%;
            width: ${(level.difficulty / 10) * 100}%;
            background: ${level.difficulty <= 3 ? '#4CAF50' : level.difficulty <= 6 ? '#FFC107' : '#F44336'};
            border-radius: 2px;
        `;
        difficultyBar.appendChild(difficultyFill);
        button.appendChild(difficultyBar);

        return button;
    }
}

